import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  Checkbox,
  Collapse,
  Typography,
  message,
  Space,
  Tag,
} from 'antd';
import {
  InboxOutlined,
  BugOutlined,
  BulbOutlined,
  QuestionCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import { useErrorBuffer } from 'providers/ErrorCapture/ErrorCaptureProvider';
import useIncidentApi from 'hooks/useIncidentApi';

const { Dragger } = Upload;
const { TextArea } = Input;

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 25 * 1024 * 1024;
const ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,application/zip,.log';

const CATEGORY_OPTIONS = [
  { value: 'bug', label: 'Bug / Error', icon: <BugOutlined /> },
  { value: 'suggestion', label: 'Sugerencia', icon: <BulbOutlined /> },
  { value: 'question', label: 'Pregunta', icon: <QuestionCircleOutlined /> },
  { value: 'other', label: 'Otro', icon: <FileTextOutlined /> },
];

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const ReportIncidentModal = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const { user } = useAuth() ?? {};
  const location = useLocation();
  const { errors, clear } = useErrorBuffer();
  const { create } = useIncidentApi();

  const [fileList, setFileList] = useState([]);
  const [selectedErrorIds, setSelectedErrorIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Al abrir el modal, marcar por default todos los errores capturados (el
  // usuario puede des-marcar los que no quiera incluir).
  useEffect(() => {
    if (open) {
      setSelectedErrorIds(errors.map((e) => e.id));
    }
  }, [open, errors]);

  const selectedErrors = useMemo(
    () => errors.filter((e) => selectedErrorIds.includes(e.id)),
    [errors, selectedErrorIds]
  );

  const handleClose = () => {
    if (submitting) return;
    form.resetFields();
    setFileList([]);
    setSelectedErrorIds([]);
    onClose?.();
  };

  const beforeUpload = (file, newList) => {
    // newList = archivos seleccionados en este batch; fileList = ya acumulados
    const upcomingCount = fileList.length + newList.length;
    if (upcomingCount > MAX_FILES) {
      message.error(`Máximo ${MAX_FILES} archivos por reporte.`);
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error(`${file.name} excede el tamaño máximo de 10 MB.`);
      return Upload.LIST_IGNORE;
    }
    const currentTotal = fileList.reduce((s, f) => s + (f.size ?? 0), 0);
    const batchTotal = newList.reduce((s, f) => s + (f.size ?? 0), 0);
    if (currentTotal + batchTotal > MAX_TOTAL_SIZE) {
      message.error('El tamaño total de adjuntos excede 25 MB.');
      return Upload.LIST_IGNORE;
    }
    return false; // false = no auto-upload; los subimos nosotros al hacer submit
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const metadata = {
        errors: selectedErrors,
        route: `${location.pathname}${location.search}`,
        screen: {
          w: window.innerWidth,
          h: window.innerHeight,
        },
      };

      const files = fileList.map((f) => f.originFileObj).filter((f) => f instanceof File);

      const result = await create({
        category: values.category,
        subject: values.subject,
        description: values.description,
        url: window.location.href,
        userAgent: navigator.userAgent,
        tenantName: user?.tenant?.name ?? null,
        metadata,
        files,
      });

      const id = result?.incidentId;
      message.success(
        id
          ? `Reporte #${id} enviado. Te contactaremos pronto.`
          : 'Reporte enviado. Te contactaremos pronto.'
      );
      clear();
      form.resetFields();
      setFileList([]);
      setSelectedErrorIds([]);
      onClose?.();
    } catch (err) {
      // Errores de form.validateFields no tienen response
      if (err?.errorFields) return;
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || 'Error al enviar el reporte';
      if (status === 429) {
        message.warning(msg || 'Superaste el límite de reportes por hora.');
      } else if (status === 400) {
        message.error(msg);
      } else {
        message.error('No pudimos enviar tu reporte. Intentá de nuevo en unos segundos.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Reportar un problema o sugerencia"
      open={open}
      onCancel={handleClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit}>
          Enviar reporte
        </Button>,
      ]}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" initialValues={{ category: 'bug' }} disabled={submitting}>
        <Form.Item
          label="Categoría"
          name="category"
          rules={[{ required: true, message: 'Elegí una categoría' }]}
        >
          <Select
            options={CATEGORY_OPTIONS.map((o) => ({
              value: o.value,
              label: (
                <Space>
                  {o.icon}
                  {o.label}
                </Space>
              ),
            }))}
          />
        </Form.Item>

        <Form.Item
          label="Asunto"
          name="subject"
          rules={[
            { required: true, message: 'El asunto es obligatorio' },
            { max: 180, message: 'Máximo 180 caracteres' },
          ]}
        >
          <Input placeholder="Ej: No puedo guardar el presupuesto" maxLength={180} showCount />
        </Form.Item>

        <Form.Item
          label="Descripción"
          name="description"
          rules={[{ required: true, message: 'Contanos un poco más' }]}
        >
          <TextArea
            rows={5}
            placeholder="¿Qué estabas haciendo? ¿Qué esperabas que pasara? ¿Qué pasó en su lugar?"
          />
        </Form.Item>

        <Form.Item label={`Adjuntos (opcional, máx ${MAX_FILES})`}>
          <Dragger
            multiple
            accept={ACCEPT}
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={({ fileList: fl }) => setFileList(fl)}
            onRemove={(file) => {
              setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Arrastrá archivos o hacé click para seleccionar</p>
            <p className="ant-upload-hint" style={{ fontSize: 12 }}>
              Imágenes, PDF, logs, zip. Máx 10 MB por archivo, 25 MB total.
            </p>
          </Dragger>
        </Form.Item>

        {errors.length > 0 && (
          <Collapse
            size="small"
            items={[
              {
                key: 'errors',
                label: (
                  <Space>
                    <span>Errores técnicos detectados en esta sesión</span>
                    <Tag color="red">{errors.length}</Tag>
                  </Space>
                ),
                children: (
                  <Checkbox.Group
                    value={selectedErrorIds}
                    onChange={setSelectedErrorIds}
                    style={{ width: '100%' }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {errors.map((err) => (
                        <div
                          key={err.id}
                          style={{
                            border: '1px solid #eee',
                            borderRadius: 6,
                            padding: 8,
                            background: '#fafafa',
                          }}
                        >
                          <Checkbox value={err.id}>
                            <Typography.Text strong style={{ fontSize: 12 }}>
                              [{err.type}] {err.message}
                            </Typography.Text>
                          </Checkbox>
                          <div
                            style={{
                              fontSize: 11,
                              color: '#888',
                              marginLeft: 24,
                              marginTop: 2,
                            }}
                          >
                            {new Date(err.timestamp).toLocaleString()} — {err.url}
                          </div>
                          {err.stack && (
                            <details style={{ marginLeft: 24, marginTop: 4 }}>
                              <summary style={{ fontSize: 11, cursor: 'pointer' }}>
                                Ver stack
                              </summary>
                              <pre
                                style={{
                                  fontSize: 10,
                                  background: '#f5f5f5',
                                  padding: 6,
                                  borderRadius: 4,
                                  maxHeight: 140,
                                  overflow: 'auto',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {err.stack}
                              </pre>
                            </details>
                          )}
                        </div>
                      ))}
                    </Space>
                  </Checkbox.Group>
                ),
              },
            ]}
          />
        )}

        {fileList.length > 0 && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {fileList.length} archivo(s) — total{' '}
            {formatBytes(fileList.reduce((s, f) => s + (f.size ?? 0), 0))}
          </Typography.Text>
        )}
      </Form>
    </Modal>
  );
};

export default ReportIncidentModal;
